'use client';

import { useEffect, useRef } from 'react';

/**
 * Tells the server the visitor is still on the form, so "abandoned" means something (see src/lib/abandoned-leads.ts):
 *   - a ping on every step change,
 *   - at most one ping a minute while they interact (typing, tapping, scrolling), so a long step is not mistaken for leaving,
 *   - a "left" signal when the page is closed or navigated away (sendBeacon, which survives the page going away).
 * Nothing here can break the form: every call swallows its errors, nothing waits on it, and no personal data is sent (draft id, step id, screen width, whether it has touch).
 * Opening the form with ?test=1 marks the draft as a staff test for the rest of the session: it will never trigger the abandoned email.
 */
const ENDPOINT = '/api/lead-activity';
const INTERACTION_THROTTLE_MS = 60_000;

function isTestSession(): boolean {
  try {
    if (new URLSearchParams(window.location.search).get('test') === '1') sessionStorage.setItem('lp_test', '1');
    return sessionStorage.getItem('lp_test') === '1';
  } catch {
    return false;
  }
}

export function useLeadActivity(draftId: string | null | undefined, stepId: string | null | undefined): void {
  const latest = useRef({ draftId, stepId });
  latest.current = { draftId, stepId };
  const lastPing = useRef(0);

  const post = (event: 'step' | 'active' | 'left') => {
    const { draftId: d, stepId: s } = latest.current;
    if (!d) return;
    try {
      const body = JSON.stringify({ draftId: d, event, step: s || undefined, test: isTestSession() || undefined, viewport: window.innerWidth, touch: navigator.maxTouchPoints });
      if (event === 'left' && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        if (navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }))) return;
      }
      void fetch(ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
    } catch {
      /* never disturb the visitor */
    }
  };

  // every step change
  useEffect(() => {
    if (!draftId || !stepId) return;
    lastPing.current = Date.now();
    post('step');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId, stepId]);

  // interaction pings, and the "left" / "back again" signals
  useEffect(() => {
    const onInteract = () => {
      const now = Date.now();
      if (now - lastPing.current < INTERACTION_THROTTLE_MS) return;
      lastPing.current = now;
      post('active');
    };
    const onLeave = () => post('left');
    const onShow = (e: PageTransitionEvent) => { if (e.persisted) { lastPing.current = Date.now(); post('active'); } };
    const opts = { passive: true } as AddEventListenerOptions;
    const events: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'touchstart', 'scroll'];
    events.forEach((ev) => window.addEventListener(ev, onInteract, opts));
    window.addEventListener('pagehide', onLeave);
    window.addEventListener('pageshow', onShow);
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, onInteract));
      window.removeEventListener('pagehide', onLeave);
      window.removeEventListener('pageshow', onShow);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
